import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  Download,
  Filter,
  Loader2,
  Save,
  ShieldMinus,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import type { HoldBackProductionType } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import RoleGuard from '@/components/auth/RoleGuard';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageErrorState } from '@/components/shared/PageErrorState';
import { BackToTopPill } from '@/components/BackToTopPill';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { PageLoader } from '@/components/loading/PageLoader';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { resolveInventoryPushSettings } from '@/features/organization/components/settingsShared';
import { useHoldBack } from '@/features/inventory/hooks/useHoldBack';
import { HoldBackConfirmDialog } from '@/features/inventory/components/HoldBackConfirmDialog';
import { HoldBackImportDialog } from '@/features/inventory/components/HoldBackImportDialog';
import { ProductionTypeBadge } from '@/features/inventory/components/ProductionTypeBadge';
import {
  HOLD_BACK_KEY_SEP,
  buildHoldBackCsv,
  buildHoldBackCsvFilename,
  buildLocationsByName,
  holdBackRuleKey,
  parseHoldBackCsv,
  type HoldBackCsvCell,
  type ParseHoldBackCsvResult,
} from '@/features/inventory/utils/holdBackCsv';
import { downloadCsvFile } from '@/lib/download';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

/**
 * Stock Hold-Back — units kept off Shopify per production type, product type
 * and location.
 *
 * LAYOUT: a type x location MATRIX, not a location switcher. The data is
 * genuinely 2-dimensional and small (~20 types x ~5 locations of one- or
 * two-digit numbers), and the value of the surface is the COMPARISON — seeing
 * that one region is 10 where everywhere else is 30 is the whole point. A
 * switcher hides exactly that, and makes "did I remember Berlin?" unanswerable
 * without five navigations. Above 6 locations the matrix stops fitting, so the
 * page falls back to one-location-at-a-time (same 6-column threshold DESIGN
 * §13.9 already uses for Select-vs-Combobox).
 *
 * PRODUCTION-TYPE AXIS (BLU-1856): the layer-2 key is
 * (location, production type, product type), so "T-Shirt" is TWO independent
 * rules — a POD one that holds back blank garments waiting to be printed, and a
 * PAP one that holds back finished units on the shelf. They are rendered as two
 * row groups inside ONE table (DESIGN §14.1: never one table per group, or the
 * location columns stop lining up across the split).
 *
 * This page owns its own location axis and deliberately does NOT read or write
 * `selectedLocation` beyond seeding: that value persists to localStorage and
 * drives /inventory and the CSV upload modals, so a settings page that
 * silently repoints it is a bug factory.
 */

/**
 * A pending edit, stored as the RAW input text rather than a parsed number, so
 * an out-of-range or non-integer entry stays on screen to be corrected instead
 * of being silently reverted to the stored value by the controlled input. '' is
 * "cleared" (no row ⇒ falls back to the org buffer).
 */
type PendingMap = Map<string, string>;

/** The server's row shape, taken from the hook so it cannot drift from tRPC. */
type HoldBackRule = ReturnType<typeof useHoldBack>['rules'][number];

/** Matches the Zod bound on `saveHoldBackRules` (`.int().min(0).max(100000)`). */
const MAX_HOLD_BACK = 100000;

/** One class string for the cell state token AND the legend, so they match. */
const CELL_META_CLASS = 'text-[10px] font-medium text-muted-foreground';

/**
 * One class string for all three section triggers. Brand-purple active state so
 * the pill reads as a first-class control rather than chrome (DESIGN §1: one
 * accent, doing real work). `motion-reduce:transition-none` because shadcn's
 * TabsTrigger ships a bare `transition-all` (DESIGN §16).
 */
const SECTION_TRIGGER_CLASS =
  'h-6 px-3 text-xs motion-reduce:transition-none data-[state=active]:bg-threadheads-purple data-[state=active]:text-white data-[state=active]:shadow-sm';

/**
 * The two row groups, in fulfilment order: POD first (the printed path, where
 * the number holds back BLANKS) then PAP (finished stock). Literal key strings
 * rather than a template so the i18n reference scan sees them.
 */
const SECTIONS = [
  {
    productionType: 'POD' as const,
    titleKey: 'holdBack.section.pod',
    descKey: 'holdBack.section.podDesc',
  },
  {
    productionType: 'PAP' as const,
    titleKey: 'holdBack.section.pap',
    descKey: 'holdBack.section.papDesc',
  },
];

/**
 * The section FILTER, layered on top of the row groups (never replacing them —
 * DESIGN §12.1.1). `all` restores the grouped view.
 *
 * Lives in the URL under `?tab=`, the same parameter every other inventory
 * list uses for its view switch, and deliberately NOT in localStorage: the page
 * comment above already explains why this surface refuses to write
 * operator-wide persisted preferences, and a key here would be read by nothing
 * else while surprising the operator on their next visit.
 *
 * There is no constant default. The default is the first section that actually
 * HAS rows (see `defaultSectionFilter`): a fixed 'pod' lands an org whose
 * catalogue is entirely PAP on an empty grid, which reads as a broken page
 * rather than as a filter.
 */
const SECTION_FILTERS = ['pod', 'pap', 'all'] as const;
type SectionFilter = (typeof SECTION_FILTERS)[number];
const SECTION_FILTER_PARAM = 'tab';

/**
 * Refuses a file before reading it, mirroring `useCsvParser`'s guard. A hold-back
 * export of a very large catalogue is a few hundred KB; anything past this is a
 * mis-picked file, and `file.text()` on it freezes the tab before the parser
 * ever gets to say no.
 */
const MAX_CSV_BYTES = 5 * 1024 * 1024;

/** The filter value that shows a given production type's group, and back. */
const filterForProductionType = (productionType: HoldBackProductionType): SectionFilter =>
  productionType === 'POD' ? 'pod' : 'pap';

/**
 * NUL, written as an escape and never as a literal byte — a literal renders as
 * an ordinary space in every diff and review. It matters here because
 * product_type is free text and routinely contains spaces ("Bath Mat"), so a
 * space separator would split the key in the wrong place. Neither of the other
 * two key parts (a UUID, and the 'POD'|'PAP' enum) can contain one, so the
 * first two separators always land in the right place and the remainder is the
 * product type verbatim.
 */
const CELL_SEP = HOLD_BACK_KEY_SEP;
const cellKey = (
  locationId: string,
  productionType: HoldBackProductionType,
  productType: string
) => `${locationId}${CELL_SEP}${productionType}${CELL_SEP}${productType}`;

/**
 * Identity of a rule ROW (no location) — the grid's row key and lookup key.
 *
 * Imported rather than re-declared: `parseHoldBackCsv` resolves an imported
 * row against `ruleIndex`'s keys, so a second definition here would be a
 * silent drift waiting to reject every row of a valid file.
 */
const ruleKey = holdBackRuleKey;

function parseCellKey(key: string): {
  locationId: string;
  productionType: HoldBackProductionType;
  productType: string;
} | null {
  const first = key.indexOf(CELL_SEP);
  if (first < 0) return null;
  const second = key.indexOf(CELL_SEP, first + CELL_SEP.length);
  if (second < 0) return null;
  return {
    locationId: key.slice(0, first),
    productionType: key.slice(first + CELL_SEP.length, second) as HoldBackProductionType,
    productType: key.slice(second + CELL_SEP.length),
  };
}

/** Blank is valid (clears the rule); anything else must be a whole number in range. */
function parseCellText(text: string): { valid: boolean; value: number | null } {
  const trimmed = text.trim();
  if (trimmed === '') return { valid: true, value: null };
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_HOLD_BACK) {
    return { valid: false, value: null };
  }
  return { valid: true, value: parsed };
}

function StockHoldBackContent() {
  const { t } = useTranslation('inventory');
  // BOTH lists. `locations` (delegated rows dropped) is the COLUMN axis: a
  // shadow location holds nothing back, so a column there would be a column of
  // inert cells. `allLocations` is for NAMING — the diagnosis panel below has
  // to be able to say which location a stored rule sits at, including one whose
  // fulfilment has since been delegated (BLU-879 review).
  const { locations, allLocations, selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { rules, isLoading, isFetching, loadError, refetch, saveHoldBackRules } = useHoldBack();

  usePageTitle(t('holdBack.title'), t('holdBack.subtitle'));

  // What a blank cell actually resolves to. Free: org settings are already in
  // context, no extra fetch. Only quoted when the PERCENT buffer is off — with
  // both set the push takes the larger of the two, so naming the units figure
  // alone would understate what a blank cell holds back.
  const pushSettings = useMemo(
    () => resolveInventoryPushSettings(organization?.settings),
    [organization?.settings]
  );
  const fallbackUnits =
    pushSettings.safetyBufferPercent == null || pushSettings.safetyBufferPercent === 0
      ? pushSettings.safetyBuffer
      : null;

  const activeLocations = useMemo(
    () => locations.filter((l) => l.is_active !== false),
    [locations]
  );
  const matrixFits = activeLocations.length > 0 && activeLocations.length <= 6;
  const [singleLocationId, setSingleLocationId] = useState<string | null>(null);
  useEffect(() => {
    // Seed from the operator's current location once, then never write back.
    if (singleLocationId === null && selectedLocation) setSingleLocationId(selectedLocation.id);
  }, [selectedLocation, singleLocationId]);

  // Clamp to a location that actually exists and is active: the seed comes from
  // a localStorage-backed preference, so a deactivated (or since-revoked)
  // location would otherwise select nothing and render a matrix with zero
  // columns — a page that looks like it has no locations at all.
  const resolvedSingleLocationId = useMemo(() => {
    const match = activeLocations.find((l) => l.id === singleLocationId);
    return (match ?? activeLocations[0])?.id ?? null;
  }, [activeLocations, singleLocationId]);

  const shownLocations = matrixFits
    ? activeLocations
    : activeLocations.filter((l) => l.id === resolvedSingleLocationId);

  const [searchParams, setSearchParams] = useSearchParams();
  const [pending, setPending] = useState<PendingMap>(new Map());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orphansOpen, setOrphansOpen] = useState(false);
  // Keyed by ruleKey (a whole orphaned rule) or cellKey (one unreachable row),
  // so two rows in the same panel can never share a spinner.
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  // Announced only on save / reset. A live region wired to the dirty COUNT
  // re-announces on every keystroke, which turns a screen reader into a
  // metronome while the operator is typing.
  const [announcement, setAnnouncement] = useState('');

  const cellRefs = useRef(new Map<string, HTMLInputElement | null>());
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const orphanToggleRef = useRef<HTMLButtonElement>(null);

  // Clear-then-set: a live region is only announced when its text actually
  // changes, so saving twice with the same outcome would otherwise be silent.
  // The handle is kept so an unmount mid-flight cannot set state on a dead
  // component (and so two announcements in a row cannot race).
  const announceTimerRef = useRef<number | null>(null);
  const announce = useCallback((message: string) => {
    setAnnouncement('');
    if (announceTimerRef.current !== null) window.clearTimeout(announceTimerRef.current);
    announceTimerRef.current = window.setTimeout(() => setAnnouncement(message), 0);
  }, []);
  useEffect(
    () => () => {
      if (announceTimerRef.current !== null) window.clearTimeout(announceTimerRef.current);
    },
    []
  );

  /**
   * One pass over the server rows into per-rule lookups, rather than a
   * `rules.find()` inside every cell: with the production-type axis the grid is
   * now up to 2x the rows it was, and the old linear scan ran once per cell per
   * render.
   *
   * `stock` is the PER-LOCATION SKU count. A location missing from it (or at 0)
   * stocks nothing of that type, so a rule there binds nothing — see
   * `cellStateFor`.
   */
  const ruleIndex = useMemo(() => {
    const index = new Map<string, { stored: Map<string, number>; stock: Map<string, number> }>();
    for (const rule of rules) {
      index.set(ruleKey(rule.productionType, rule.productType), {
        stored: new Map(rule.byLocation.map((b) => [b.locationId, b.bufferUnits])),
        stock: new Map(rule.skuCountByLocation.map((s) => [s.locationId, s.skuCount])),
      });
    }
    return index;
  }, [rules]);

  const storedText = useCallback(
    (locationId: string, productionType: HoldBackProductionType, productType: string): string => {
      const stored = ruleIndex.get(ruleKey(productionType, productType))?.stored.get(locationId);
      return stored === undefined ? '' : String(stored);
    },
    [ruleIndex]
  );

  /**
   * Whether this (location, rule) intersection is worth typing into.
   *
   * A type with no stocked SKUs at a location has nothing for a buffer to hold
   * back, so the cell is inert and reads as disabled. A STORED number there is
   * a different thing — stale, left behind by a transfer or a rename — and must
   * stay visible AND clearable, so the presence of a stored row re-enables the
   * cell even with no stock behind it.
   */
  const cellStateFor = useCallback(
    (locationId: string, rule: HoldBackRule) => {
      const entry = ruleIndex.get(ruleKey(rule.productionType, rule.productType));
      const stored = entry?.stored.get(locationId);
      const hasStock = (entry?.stock.get(locationId) ?? 0) > 0;
      const hasStored = stored !== undefined;
      return { hasStock, hasStored, editable: hasStock || hasStored };
    },
    [ruleIndex]
  );

  const cellText = (
    locationId: string,
    productionType: HoldBackProductionType,
    productType: string
  ): string =>
    pending.get(cellKey(locationId, productionType, productType)) ??
    storedText(locationId, productionType, productType);

  const setCell = (
    locationId: string,
    productionType: HoldBackProductionType,
    productType: string,
    raw: string
  ) => {
    const key = cellKey(locationId, productionType, productType);
    setPending((prev) => {
      const copy = new Map(prev);
      // Blank and 0 are NOT the same thing here: blank clears the rule (fall
      // back to the org buffer), 0 is an explicit "hold back nothing".
      if (raw.trim() === storedText(locationId, productionType, productType)) copy.delete(key);
      else copy.set(key, raw);
      return copy;
    });
  };

  const { changedCells, invalidKeys } = useMemo(() => {
    const changed: Array<{
      locationId: string;
      productionType: HoldBackProductionType;
      productType: string;
      bufferUnits: number | null;
    }> = [];
    const invalid = new Set<string>();
    for (const [key, text] of pending.entries()) {
      const parsed = parseCellKey(key);
      if (!parsed) continue;
      const { valid, value } = parseCellText(text);
      if (!valid) {
        invalid.add(key);
        continue;
      }
      changed.push({ ...parsed, bufferUnits: value });
    }
    return { changedCells: changed, invalidKeys: invalid };
  }, [pending]);

  const isDirty = pending.size > 0;
  const canSave = changedCells.length > 0 && invalidKeys.size === 0 && !saveHoldBackRules.isPending;

  // A half-typed matrix must not be lost to a reload or a tab close.
  useEffect(() => {
    if (!isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const activeRules = useMemo(() => rules.filter((r) => !r.isOrphaned), [rules]);
  const orphanedRules = useMemo(() => rules.filter((r) => r.isOrphaned), [rules]);

  /**
   * Row counts per production type, over ALL active rules — never the filtered
   * view. They ride on the segmented pill, which is the only place a `PAP 0`
   * can be said out loud: a filter that silently shows nothing is
   * indistinguishable from a page that failed to load.
   */
  const sectionCounts = useMemo(
    () => ({
      pod: activeRules.filter((r) => r.productionType === 'POD').length,
      pap: activeRules.filter((r) => r.productionType === 'PAP').length,
    }),
    [activeRules]
  );

  /**
   * Which production-type group is on screen. URL-persisted so a link to this
   * page can name the section, and so a browser Back after the blocked-Save
   * escape hatch below behaves.
   *
   * The default is the first section that HAS rows, so a catalogue that is
   * entirely PAP opens on PAP instead of on an empty POD grid. Anything
   * unrecognised in the query string resolves to that same default rather than
   * erroring: a hand-edited URL must never blank the grid.
   */
  const defaultSectionFilter: SectionFilter =
    sectionCounts.pod > 0 ? 'pod' : sectionCounts.pap > 0 ? 'pap' : 'all';
  const sectionParam = searchParams.get(SECTION_FILTER_PARAM)?.trim().toLowerCase();
  const sectionFilter: SectionFilter = SECTION_FILTERS.includes(sectionParam as SectionFilter)
    ? (sectionParam as SectionFilter)
    : defaultSectionFilter;
  const setSectionFilter = useCallback(
    (value: SectionFilter) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(SECTION_FILTER_PARAM, value);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /**
   * Stored rows at locations the caller can no longer reach (a client junction
   * revoked, a `profile_locations` grant removed). They have no column in the
   * grid — the location is not in `locations` at all — so without this they
   * would be invisible while still holding stock off the storefront.
   *
   * Orphaned rules are skipped: their whole-rule Remove already deletes every
   * row including these, and listing one row twice with two Remove buttons
   * reads as two separate problems.
   */
  const inaccessibleRows = useMemo(
    () =>
      rules
        .filter((r) => !r.isOrphaned)
        .flatMap((r) =>
          r.byLocation
            .filter((b) => b.inaccessible)
            .map((b) => ({
              productionType: r.productionType,
              productType: r.productType,
              locationId: b.locationId,
              bufferUnits: b.bufferUnits,
            }))
        ),
    [rules]
  );

  /**
   * Stored rows at locations the org still reaches but has DELEGATED: their
   * fulfilment is routed to a shared 3PL location, so the inventory push worker
   * skips them and the number holds nothing back. They have no column for the
   * same reason, so without this group they are stored, inert and invisible.
   *
   * Distinct from `inaccessibleRows` on purpose, and that group is deliberately
   * NOT widened to cover them: "you no longer manage this location" and "this
   * location's fulfilment happens elsewhere" are different facts with different
   * fixes, and one heading covering both would be true of neither.
   *
   * Computed client-side by set difference (`allLocations` minus `locations`)
   * rather than from a new server field: the backend has no idea this grid drew
   * a column axis, and adding a flag to the rule payload for a presentation
   * concern is how a read model starts carrying view state.
   */
  const delegatedLocationIds = useMemo(() => {
    const selectable = new Set(locations.map((l) => l.id));
    return new Set(allLocations.filter((l) => !selectable.has(l.id)).map((l) => l.id));
  }, [allLocations, locations]);

  const delegatedRows = useMemo(
    () =>
      delegatedLocationIds.size === 0
        ? []
        : rules
            .filter((r) => !r.isOrphaned)
            .flatMap((r) =>
              r.byLocation
                // `inaccessible` wins: a row cannot be both unreachable and
                // merely delegated, and listing it twice reads as two problems.
                .filter((b) => !b.inaccessible && delegatedLocationIds.has(b.locationId))
                .map((b) => ({
                  productionType: r.productionType,
                  productType: r.productType,
                  locationId: b.locationId,
                  bufferUnits: b.bufferUnits,
                }))
            ),
    [rules, delegatedLocationIds]
  );

  // Row groups, in fulfilment order. The server already sorts POD first, but
  // the grouping is done here so the split is a property of the LAYOUT and not
  // of whatever order the payload happened to arrive in.
  const allSections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        rules: activeRules.filter((r) => r.productionType === section.productionType),
      })).filter((section) => section.rules.length > 0),
    [activeRules]
  );
  const sections = useMemo(
    () =>
      sectionFilter === 'all'
        ? allSections
        : allSections.filter((s) => filterForProductionType(s.productionType) === sectionFilter),
    [allSections, sectionFilter]
  );
  // Section order, flattened: the Enter-walk and the invalid-cell hunt both
  // travel the visual order, across the section break. `sectionOffsets` turns a
  // (section, row) pair into that flat index without an O(n) `indexOf` per row.
  const flatRules = useMemo(() => sections.flatMap((s) => s.rules), [sections]);
  // The SAME flattening over the UNFILTERED groups. The invalid-cell hunt below
  // must travel this one: an invalid cell in a hidden section would otherwise
  // leave Save disabled with nothing red on screen and no route back to it
  // (DESIGN §12.1's blocked-Save rule).
  const allFlatRules = useMemo(() => allSections.flatMap((s) => s.rules), [allSections]);
  const sectionOffsets = useMemo(() => {
    let acc = 0;
    return sections.map((s) => {
      const start = acc;
      acc += s.rules.length;
      return start;
    });
  }, [sections]);

  // NAMING, so `allLocations`: a rule can sit at a location whose fulfilment
  // has since been delegated. That location has no column (delegated rows are
  // not part of the grid axis) but it still has a name, and printing
  // "Unknown location" for a place the org runs is worse than useless.
  const locationName = useCallback(
    (locationId: string) =>
      allLocations.find((l) => l.id === locationId)?.name ?? t('holdBack.unknownLocation'),
    [allLocations, t]
  );

  /**
   * Label for a location that is NOT in `allLocations` — by definition every
   * inaccessible row. "Unknown location" repeated five times identifies
   * nothing; a short id is at least a handle the operator can quote.
   */
  const locationLabelOrId = useCallback(
    (locationId: string) =>
      allLocations.find((l) => l.id === locationId)?.name ??
      t('holdBack.locationShortId', { id: locationId.slice(0, 8) }),
    [allLocations, t]
  );

  // Which locations hold a cell that Save is refusing. Ordered by the page's
  // own location axis so the message reads in the same order as the columns.
  const invalidLocationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const key of invalidKeys) {
      const parsed = parseCellKey(key);
      if (parsed) ids.add(parsed.locationId);
    }
    return activeLocations.filter((l) => ids.has(l.id)).map((l) => l.id);
  }, [invalidKeys, activeLocations]);

  // Hunted over `allFlatRules`, NOT the filtered `flatRules`: the offending cell
  // may sit in a section the operator has filtered away, and a Save that is
  // disabled by a cell the page will not admit exists is a dead end.
  const firstInvalidCell = useMemo(() => {
    for (const locationId of invalidLocationIds) {
      for (const rule of allFlatRules) {
        const key = cellKey(locationId, rule.productionType, rule.productType);
        if (invalidKeys.has(key)) {
          return { locationId, key, productionType: rule.productionType };
        }
      }
    }
    return null;
  }, [invalidLocationIds, allFlatRules, invalidKeys]);

  // In single-location mode the offending cell can sit at a location that is
  // not on screen, and with a section filter it can sit in a hidden group:
  // Save is disabled with nothing red anywhere. The counter is the way back to
  // it — switch both axes, then focus the input. Ref callbacks run during the
  // same commit as those changes, so by the time this effect fires the input is
  // mounted and registered.
  const [focusCellKey, setFocusCellKey] = useState<string | null>(null);
  useEffect(() => {
    if (!focusCellKey) return;
    const el = cellRefs.current.get(focusCellKey);
    el?.focus();
    el?.select();
    setFocusCellKey(null);
  }, [focusCellKey]);

  const goToFirstInvalid = () => {
    if (!firstInvalidCell) return;
    if (!matrixFits) setSingleLocationId(firstInvalidCell.locationId);
    const targetSection = filterForProductionType(firstInvalidCell.productionType);
    if (sectionFilter !== 'all' && sectionFilter !== targetSection) {
      setSectionFilter(targetSection);
    }
    setFocusCellKey(firstInvalidCell.key);
  };

  const handleReset = () => {
    setPending(new Map());
    announce(t('holdBack.announceReset'));
  };

  // ── CSV round trip ────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<ParseHoldBackCsvResult | null>(null);

  const storedUnitsFor = useCallback(
    (locationId: string, productionType: HoldBackProductionType, productType: string) =>
      ruleIndex.get(ruleKey(productionType, productType))?.stored.get(locationId) ?? null,
    [ruleIndex]
  );

  /** Drives the export's `stocked` column — the grid's inert-cell fact, flattened. */
  const hasStockAt = useCallback(
    (locationId: string, productionType: HoldBackProductionType, productType: string) =>
      (ruleIndex.get(ruleKey(productionType, productType))?.stock.get(locationId) ?? 0) > 0,
    [ruleIndex]
  );

  /**
   * What the grid currently shows for a cell, PENDING EDITS INCLUDED. Feeds the
   * import's changed-cell ceiling: a re-import that matches what is on screen
   * changes nothing and must not be refused for being large.
   *
   * An unparseable half-typed cell returns NaN, which compares unequal to every
   * imported value — staging over it IS a change, and saying so this way needs
   * no third state in the parser's signature.
   */
  const currentUnitsAt = useCallback(
    (locationId: string, productionType: HoldBackProductionType, productType: string) => {
      const parsed = parseCellText(
        pending.get(cellKey(locationId, productionType, productType)) ??
          storedText(locationId, productionType, productType)
      );
      return parsed.valid ? parsed.value : Number.NaN;
    },
    [pending, storedText]
  );

  /**
   * Exports the WHOLE grid: every active location and BOTH production types,
   * regardless of the `?tab=` filter or single-location mode.
   *
   * This file is a round-trip document, not a screenshot of the current view.
   * Exporting the filtered view would produce a file that, re-imported, looks
   * complete while silently describing a subset — the same failure the
   * filter-and-count-at-the-same-layer rule exists to prevent, and worse here
   * because the artefact outlives the session.
   */
  const handleExport = () => {
    const csv = buildHoldBackCsv({
      rules: allFlatRules,
      locations: activeLocations.map((l) => ({ id: l.id, name: l.name })),
      storedFor: storedUnitsFor,
      hasStockFor: hasStockAt,
    });
    downloadCsvFile(csv, buildHoldBackCsvFilename(new Date().toISOString().slice(0, 10)));
  };

  const handleFileChosen = async (event: ReactChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared immediately so picking the SAME file twice still fires a change.
    event.target.value = '';
    if (!file) return;

    // Refused BEFORE `file.text()`: reading a multi-hundred-MB file into a
    // string freezes the tab, and a frozen tab cannot show an error. Mirrors
    // `useCsvParser`'s identical guard rather than inventing a second limit.
    if (file.size > MAX_CSV_BYTES) {
      toast({
        title: t('holdBack.csv.fileTooLargeTitle'),
        description: t('holdBack.csv.fileTooLargeDesc', {
          limit: Math.round(MAX_CSV_BYTES / (1024 * 1024)),
        }),
        variant: 'destructive',
      });
      return;
    }

    let text: string;
    try {
      text = await file.text();
    } catch {
      toast({
        title: t('holdBack.csv.readFailedTitle'),
        description: t('holdBack.csv.readFailedDesc'),
        variant: 'destructive',
      });
      return;
    }

    setImportResult(
      parseHoldBackCsv(text, {
        // A delegated location's name is simply absent from this map, so its
        // rows land in `rejected` as unknown_location. Deliberately NOT plumbed
        // back in to produce a more specific message: reintroducing delegated
        // locations into the grid's data path to improve one error string
        // reopens exactly the bug BLU-879 closes. The message names the
        // locations that ARE accepted instead.
        locationsByName: buildLocationsByName(activeLocations),
        ruleKeys: new Set(ruleIndex.keys()),
        isCellEditable: (locationId, productionType, productType) => {
          const entry = ruleIndex.get(ruleKey(productionType, productType));
          if (!entry) return false;
          return (entry.stock.get(locationId) ?? 0) > 0 || entry.stored.has(locationId);
        },
        currentUnitsFor: currentUnitsAt,
      })
    );
    setImportOpen(true);
  };

  /**
   * Stage accepted cells through the grid's own `setCell`, so a value equal to
   * the stored one drops straight back out of `pending` rather than becoming a
   * phantom dirty cell that Save would send as a no-op write. That is what makes
   * re-importing an unmodified export produce zero pending edits.
   */
  const handleStageImport = (cells: HoldBackCsvCell[]) => {
    for (const cell of cells) {
      setCell(
        cell.locationId,
        cell.productionType,
        cell.productType,
        cell.bufferUnits === null ? '' : String(cell.bufferUnits)
      );
    }
    // If anything staged sits outside the current filter, widen to All: a dirty
    // count that does not correspond to rows the operator can look at is the
    // unreachable-blocked-Save problem in a different costume.
    if (
      sectionFilter !== 'all' &&
      cells.some((c) => filterForProductionType(c.productionType) !== sectionFilter)
    ) {
      setSectionFilter('all');
    }
    setImportOpen(false);
    setImportResult(null);
    announce(t('holdBack.csv.announceStaged', { count: cells.length }));
  };

  const handleSave = async () => {
    const upserts = changedCells
      .filter(
        (
          c
        ): c is {
          locationId: string;
          productionType: HoldBackProductionType;
          productType: string;
          bufferUnits: number;
        } => c.bufferUnits !== null
      )
      .map((c) => ({
        locationId: c.locationId,
        productionType: c.productionType,
        productType: c.productType,
        bufferUnits: c.bufferUnits,
      }));
    const deletes = changedCells
      .filter((c) => c.bufferUnits === null)
      .map((c) => ({
        locationId: c.locationId,
        productionType: c.productionType,
        productType: c.productType,
      }));
    // Deliberately NOT caught here: the dialog awaits this and renders the
    // failure inline, because its own overlay hides the hook's toast.
    await saveHoldBackRules.mutateAsync({ upserts, deletes });
    setPending(new Map());
    setConfirmOpen(false);
    announce(t('holdBack.announceSaved'));
  };

  /**
   * Shared tail for both cleanup buttons in the diagnosis panel: the control
   * that had focus is about to unmount, so hand focus to the panel that still
   * owns the list, or to Save when the panel itself is going away.
   */
  const removalSettled = useCallback(
    (wasLastEntry: boolean) => (_data: unknown, error: unknown) => {
      setRemovingKey(null);
      const target = !error && wasLastEntry ? saveButtonRef.current : orphanToggleRef.current;
      (target ?? saveButtonRef.current)?.focus();
    },
    []
  );

  const diagnosisCount =
    orphanedRules.length + inaccessibleRows.length + delegatedRows.length;

  /**
   * Orphan cleanup. Reuses the grid's own delete path rather than a bespoke
   * endpoint: an orphaned rule is just every stored row for a (production type,
   * product type) pair that no longer matches any stocked SKU.
   */
  const handleRemoveOrphan = (rule: HoldBackRule) => {
    // Decided BEFORE the mutation: removing the last entry unmounts the whole
    // panel, so the disclosure button this focus would otherwise land on is
    // about to disappear.
    const wasLastEntry = diagnosisCount === 1;
    setRemovingKey(ruleKey(rule.productionType, rule.productType));
    saveHoldBackRules.mutate(
      {
        upserts: [],
        deletes: rule.byLocation.map((b) => ({
          locationId: b.locationId,
          productionType: rule.productionType,
          productType: rule.productType,
        })),
      },
      {
        onSuccess: () =>
          announce(
            t('holdBack.announceOrphanRemoved', {
              production: rule.productionType,
              type: rule.productType,
            })
          ),
        onSettled: removalSettled(wasLastEntry),
      }
    );
  };

  /**
   * Unreachable-row cleanup — ONE cell, delete-only. The backend deliberately
   * degrades its location gate to lenient for a delete-only payload (the row is
   * the caller's own org-scoped row; losing standing at the location does not
   * make it someone else's), which is exactly why this must never batch an
   * upsert alongside it.
   */
  const handleRemoveInaccessible = (row: (typeof inaccessibleRows)[number]) => {
    const wasLastEntry = diagnosisCount === 1;
    setRemovingKey(cellKey(row.locationId, row.productionType, row.productType));
    saveHoldBackRules.mutate(
      {
        upserts: [],
        deletes: [
          {
            locationId: row.locationId,
            productionType: row.productionType,
            productType: row.productType,
          },
        ],
      },
      {
        onSuccess: () =>
          announce(
            t('holdBack.announceInaccessibleRemoved', {
              production: row.productionType,
              type: row.productType,
              location: locationLabelOrId(row.locationId),
            })
          ),
        onSettled: removalSettled(wasLastEntry),
      }
    );
  };

  // Warehouse muscle memory, mirroring BulkStockEditorDialog: Enter walks down
  // the column and the last row hands focus to Save rather than dead-ending.
  // It STEPS OVER inert cells (a type not stocked at this location renders
  // disabled) and over the section break, so the walk never dead-ends on a gap
  // in the middle of a column.
  // Cmd/Ctrl+Enter is a PAGE shortcut (handled at the root below), so it is
  // deliberately not swallowed here.
  const handleCellKeyDown = (
    event: ReactKeyboardEvent<HTMLInputElement>,
    flatIndex: number,
    locationId: string
  ) => {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    for (let i = flatIndex + 1; i < flatRules.length; i += 1) {
      const rule = flatRules[i];
      if (!rule) continue;
      const next = cellRefs.current.get(
        cellKey(locationId, rule.productionType, rule.productType)
      );
      if (next && !next.disabled) {
        next.focus();
        next.select();
        return;
      }
    }
    saveButtonRef.current?.focus();
  };

  // Page-level shortcut: works from any cell, the diagnosis panel, or the bar
  // itself. The confirm dialog is portaled, so its own Cmd+Enter never bubbles
  // back here to re-open what is already open.
  const handlePageKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey) || !canSave) return;
    event.preventDefault();
    setConfirmOpen(true);
  };

  if (isLoading) return <PageLoader pool="holdBack" />;

  // A failed load and "nothing is held back" must never look the same on this
  // page — an empty grid would read as a claim that no stock is being withheld.
  if (loadError) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <PageErrorState
          title={t('holdBack.loadErrorTitle')}
          description={t('holdBack.loadErrorDesc')}
          onRetry={() => void refetch()}
          retryLabel={t('holdBack.retry')}
          retrying={isFetching}
        />
      </div>
    );
  }

  // Group headings appear only when more than one group is present; with a
  // single group the panel title IS that group's title, and repeating it as a
  // heading underneath reads as two levels of the same sentence.
  const diagnosisGroupCount =
    (orphanedRules.length > 0 ? 1 : 0) +
    (inaccessibleRows.length > 0 ? 1 : 0) +
    (delegatedRows.length > 0 ? 1 : 0);
  const showDiagnosisGroupHeadings = diagnosisGroupCount > 1;
  const diagnosisTitle = showDiagnosisGroupHeadings
    ? t('holdBack.diagnosisTitle', { count: diagnosisCount })
    : orphanedRules.length > 0
      ? t('holdBack.orphanTitle', { count: orphanedRules.length })
      : inaccessibleRows.length > 0
        ? t('holdBack.inaccessibleTitle', { count: inaccessibleRows.length })
        : t('holdBack.delegatedTitle', { count: delegatedRows.length });

  /**
   * The grid, or the honest reason there is none. Three states, in the order
   * they are true: nothing exists at all; the SECTION FILTER hid everything
   * that exists; the grid.
   *
   * The middle one is the state #1864 shipped without: the empty state was
   * gated on `activeRules.length` while the body mapped the FILTERED sections,
   * so an org with only PAP rules looking at the POD tab got no table and no
   * empty state either — a page that renders nothing and explains nothing
   * (DESIGN §15.2: a filtered empty is a different message from a real one,
   * and it comes with the way out).
   */
  const gridBody =
    activeRules.length === 0 ? (
      <EmptyState
        icon={ShieldMinus}
        title={t('holdBack.emptyTitle')}
        description={t('holdBack.emptyDesc')}
      />
    ) : sections.length === 0 ? (
      <EmptyState
        icon={Filter}
        title={t(
          sectionFilter === 'pap' ? 'holdBack.filteredEmptyPap' : 'holdBack.filteredEmptyPod'
        )}
        description={t('holdBack.filteredEmptyDesc')}
        action={
          <Button
            variant="outline"
            className="h-8 gap-1.5 px-3 text-xs"
            onClick={() => setSectionFilter('all')}
          >
            {t('holdBack.showAll')}
          </Button>
        }
      />
    ) : (
      <div className="rounded-md border [&>div]:overflow-visible">
        <Table>
          <TableHeader
            className="[&_th]:sticky [&_th]:top-[var(--hold-back-header-top)] [&_th]:z-10 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_0_hsl(var(--border))]"
            style={
              {
                ['--hold-back-header-top' as string]:
                  'calc(var(--top-header-height, 3.5rem) + var(--page-action-bar-height, 0px))',
              } as CSSProperties
            }
          >
            <TableRow>
              {/* shadcn's TableHead defaults to `text-muted-foreground`, which
                  made the location names — the labels the entire comparison
                  is read against — the lowest-contrast text on the page.
                  Raised to `text-foreground`; the 3PL eyebrow below stays
                  muted so the hierarchy inside the header survives. */}
              <TableHead scope="col" className="px-3 py-2.5 text-foreground">
                {t('holdBack.colProductType')}
              </TableHead>
              {shownLocations.map((l) => (
                <TableHead
                  key={l.id}
                  scope="col"
                  className="px-3 py-2.5 text-center text-foreground"
                >
                  {l.is_shared && (
                    <span className="block text-[10px] font-normal uppercase tracking-wider text-muted-foreground">
                      {t('holdBack.thirdParty', { org: l.owner_organization_name ?? '' })}
                    </span>
                  )}
                  {l.name}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          {/* One <tbody> per production type: real row-group semantics for the
              `scope="rowgroup"` header below, inside ONE <table> so the
              location columns stay aligned across the split (DESIGN §14.1). */}
          {sections.map((section, sectionIndex) => (
            <TableBody key={section.productionType}>
              {/* The section break is drawn on the TOP of the second group's
                  header, not the bottom of the first group's last row: shadcn's
                  TableBody strips the border off its own `tr:last-child`, so a
                  bottom border there would depend on class-merge order. */}
              <TableRow
                className={cn(
                  'bg-muted/30 hover:bg-muted/30',
                  sectionIndex > 0 && 'border-t'
                )}
              >
                <th
                  scope="rowgroup"
                  colSpan={1 + shownLocations.length}
                  className="px-3 py-2 text-left align-middle font-normal"
                >
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground">
                      {t(section.titleKey)}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {t(section.descKey)}
                    </span>
                    <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">
                      {t('holdBack.sectionTypeCount', { count: section.rules.length })}
                    </span>
                  </div>
                </th>
              </TableRow>
              {section.rules.map((rule, rowIndex) => {
                const flatIndex = (sectionOffsets[sectionIndex] ?? 0) + rowIndex;
                return (
                  <TableRow
                    key={ruleKey(rule.productionType, rule.productType)}
                    className="hover:bg-muted/40 motion-reduce:hover:bg-muted/40"
                  >
                    {/* Row header, not a plain cell: it names every input to
                        its right, so a screen reader reads "Bath Mat, POD,
                        Melbourne". The badge is load-bearing, not decoration —
                        the same product type appears in both sections, and the
                        group header is off screen once the operator scrolls. */}
                    <th scope="row" className="px-3 py-2.5 text-left align-middle font-normal">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">{rule.productType}</span>
                        <ProductionTypeBadge productionType={rule.productionType} />
                      </div>
                      <div className="text-[11px] tabular-nums text-muted-foreground">
                        {t('holdBack.skuCount', { count: rule.skuCount })}
                      </div>
                    </th>
                    {shownLocations.map((l, colIndex) => {
                      const key = cellKey(l.id, rule.productionType, rule.productType);
                      const text = cellText(l.id, rule.productionType, rule.productType);
                      const { valid, value } = parseCellText(text);
                      const dirty = pending.has(key);
                      const { hasStock, editable } = cellStateFor(l.id, rule);
                      // A number standing where the type is not stocked: it
                      // holds nothing back and nobody can verify it. Keyed off
                      // the TEXT, not off `editable`, so clearing the cell
                      // drops the warning instead of complaining about a
                      // number that is no longer there.
                      const stale = !hasStock && text.trim() !== '';
                      // Positional, NOT the cell key: the key carries NUL
                      // separators and free-text product types, neither of
                      // which is legal in an HTML id.
                      const metaId = `hold-back-meta-${flatIndex}-${colIndex}`;
                      // Priority: a refused value first, then "this cell binds
                      // nothing", then what a valid value resolves to.
                      const meta = !valid
                        ? t('holdBack.cellInvalid')
                        : stale
                          ? t('holdBack.staleRule')
                          : !hasStock
                            ? t('holdBack.noStockLabel')
                            : value === null
                              ? t('holdBack.fallbackLabel')
                              : value === 0
                                ? t('holdBack.noneLabel')
                                : '';
                      const metaClass = !valid
                        ? 'text-[10px] font-medium text-destructive'
                        : stale
                          ? 'text-[10px] font-medium text-amber-700'
                          : CELL_META_CLASS;
                      return (
                        <TableCell key={l.id} className="px-3 py-2.5 text-center">
                          <Input
                            ref={(el) => {
                              if (el) cellRefs.current.set(key, el);
                              else cellRefs.current.delete(key);
                            }}
                            // TEXT, not number: a `type="number"` input reports ''
                            // for an unparseable entry ("5e") while still showing
                            // it, so the cell would read "Fallback" and Save would
                            // DELETE the rule. Text keeps the raw string, and
                            // `parseCellText` does the validating.
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoComplete="off"
                            // Nothing of this type is stocked here and no stale
                            // row is left behind, so a number would bind nothing.
                            // Disabled rather than hidden: the column still has
                            // to line up, and "not applicable" is information.
                            disabled={!editable}
                            // Stale < dirty < invalid: the loudest true thing
                            // about the cell owns the border.
                            className={cn(
                              'mx-auto h-8 w-20 text-center tabular-nums',
                              stale && 'border-amber-300',
                              dirty && valid && 'border-threadheads-purple',
                              !valid && 'border-destructive ring-1 ring-destructive'
                            )}
                            // Blank input = no rule. Rendering 0 as blank would
                            // erase the difference between the two.
                            value={text}
                            placeholder="-"
                            aria-invalid={!valid || undefined}
                            aria-describedby={meta ? metaId : undefined}
                            aria-label={t('holdBack.cellAria', {
                              production: rule.productionType,
                              type: rule.productType,
                              location: l.name,
                            })}
                            onChange={(e) =>
                              setCell(l.id, rule.productionType, rule.productType, e.target.value)
                            }
                            onKeyDown={(e) => handleCellKeyDown(e, flatIndex, l.id)}
                          />
                          <div id={metaId} className={cn('mt-0.5 min-h-4', metaClass)}>
                            {meta}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          ))}
        </Table>
      </div>
    );

  return (
    // Full-width list-page padding (mirrors OrdersList / Products). The
    // responsive px-4 sm:px-6 lg:px-8 exactly cancels PageActionBar's
    // -mx-4 sm:-mx-6 lg:-mx-8 breakout at every breakpoint; without it the bar
    // bleeds past the shell edge and forces a page-level horizontal scrollbar.
    <div className="w-full px-4 py-6 sm:px-6 lg:px-8" onKeyDown={handlePageKeyDown}>
      {/* The Tabs ROOT spans the whole page, not just the pill: the TabsList
          sits in the action bar's left cluster and the TabsContent panels sit
          below the diagnosis panel, and Radix wires `aria-controls` between
          them through this shared root. Passing the list through
          PageActionBar's `left` prop is fine — props are rendered where the
          receiving component places them, which is inside this subtree. */}
      <Tabs
        value={sectionFilter}
        onValueChange={(value) => setSectionFilter(value as SectionFilter)}
        className="space-y-6"
      >
        {/* Sticky bar — FIRST child (DESIGN §3.1) and the ONLY home for actions.
            Both clusters go through `left`/`right`: PageActionBar ignores
            `children` entirely once either slot is set (DESIGN §3). */}
        <PageActionBar
          ariaLabel={t('holdBack.actionsAria')}
          left={
            <>
              {/* Section filter, FIRST in the cluster: it narrows the rows, so it
                  reads before the control that narrows the columns. Segmented
                  pill mirroring CurrencyTogglePill — brand-purple active state
                  so it reads as a first-class control, not chrome.
                  The TabsList lives here but its Tabs ROOT wraps the whole page,
                  so each trigger's aria-controls resolves to the real panel. */}
              <TabsList className="h-8 self-end" aria-label={t('holdBack.sectionFilterAria')}>
                {/* The count is the point: it is the only way the pill can say
                    "PAP 0" out loud, so an empty section reads as a fact about
                    the catalogue rather than as a page that failed to load. */}
                <TabsTrigger value="pod" className={SECTION_TRIGGER_CLASS}>
                  {t('holdBack.section.pod')}
                  <span className="ml-1.5 tabular-nums">{sectionCounts.pod}</span>
                </TabsTrigger>
                <TabsTrigger value="pap" className={SECTION_TRIGGER_CLASS}>
                  {t('holdBack.section.pap')}
                  <span className="ml-1.5 tabular-nums">{sectionCounts.pap}</span>
                </TabsTrigger>
                <TabsTrigger value="all" className={SECTION_TRIGGER_CLASS}>
                  {t('holdBack.sectionAll')}
                  <span className="ml-1.5 tabular-nums">
                    {sectionCounts.pod + sectionCounts.pap}
                  </span>
                </TabsTrigger>
              </TabsList>
              {!matrixFits && activeLocations.length > 0 && (
                <>
                  {/* Rows-filter | columns-filter: two controls narrowing
                      different axes, so DESIGN §3.3's LEFT-cluster divider
                      (taller, firmer) sits between them. */}
                  <PageActionBarDivider variant="filter" />
                  <Select
                    value={resolvedSingleLocationId ?? undefined}
                    onValueChange={setSingleLocationId}
                  >
                    <SelectTrigger
                      className="h-8 w-[200px] text-xs"
                      aria-label={t('holdBack.locationSelectAria')}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLocations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
              {invalidKeys.size > 0 ? (
                // A dead-end count is useless in single-location mode, where the
                // offending cell may be off screen entirely. This both names the
                // locations and walks the operator to the first bad cell.
                <button
                  type="button"
                  onClick={goToFirstInvalid}
                  className="self-end rounded-sm pb-1.5 text-xs tabular-nums text-destructive underline decoration-dotted underline-offset-2 transition-colors duration-200 hover:text-destructive/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
                >
                  {invalidLocationIds.length > 0
                    ? t('holdBack.invalidCountAt', {
                        count: invalidKeys.size,
                        locations: invalidLocationIds.map(locationName).join(', '),
                      })
                    : t('holdBack.invalidCount', { count: invalidKeys.size })}
                </button>
              ) : (
                <span className="self-end pb-1.5 text-xs tabular-nums text-muted-foreground">
                  {isDirty ? t('holdBack.dirtyCount', { count: pending.size }) : t('holdBack.noChanges')}
                </span>
              )}
              {/* Save / reset / cleanup outcomes only — see `announcement` above. */}
              <span role="status" className="sr-only">
                {announcement}
              </span>
            </>
          }
          right={
            // DESIGN §3.3 right-cluster order: auxiliary, divider, primary,
            // divider, the undo/destructive tail. Reset used to lead, which put
            // the one irreversible-feeling control first in the reading order.
            <>
              {/* Bulk edit path: export the whole grid, edit in a spreadsheet,
                  import back. Staging lands in `pending`; Save is unchanged. */}
              <Button
                variant="outline"
                className="h-8 gap-1.5 px-3 text-xs"
                onClick={handleExport}
                disabled={activeRules.length === 0 || activeLocations.length === 0}
              >
                <Download className="h-3.5 w-3.5" aria-hidden="true" />
                {t('holdBack.csv.export')}
              </Button>
              <Button
                variant="outline"
                className="h-8 gap-1.5 px-3 text-xs"
                onClick={() => fileInputRef.current?.click()}
                disabled={activeRules.length === 0 || activeLocations.length === 0}
              >
                <Upload className="h-3.5 w-3.5" aria-hidden="true" />
                {t('holdBack.csv.import')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
                onChange={(e) => void handleFileChosen(e)}
              />
              <PageActionBarDivider />
              <Button
                ref={saveButtonRef}
                className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
                onClick={() => setConfirmOpen(true)}
                disabled={!canSave}
                aria-keyshortcuts="Meta+Enter Control+Enter"
                aria-busy={saveHoldBackRules.isPending}
              >
                {saveHoldBackRules.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <Save className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {t('holdBack.save')}
              </Button>
              {isDirty && (
                <>
                  <PageActionBarDivider />
                  <Button
                    variant="outline"
                    className="h-8 gap-1.5 border-amber-200 px-3 text-xs text-amber-700 hover:bg-amber-50"
                    onClick={handleReset}
                    disabled={saveHoldBackRules.isPending}
                  >
                    <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('holdBack.reset')}
                  </Button>
                </>
              )}
            </>
          }
        />

        <p className="max-w-3xl text-sm text-muted-foreground">{t('holdBack.explainer')}</p>

        {/* Diagnosis panel — three ways a stored rule stops doing what it says:
            a Shopify productType rename detaches it (its SKUs go quietly back on
            sale) — orphaned; the location it sits at is no longer one this org
            can reach — inaccessible; or the org still reaches the location but
            its fulfilment has been delegated to a shared 3PL, so the rule holds
            nothing back — delegated. All three have no column above and would
            otherwise be invisible. The diagnosis leads; Remove is the demoted
            cleanup. */}
        {diagnosisCount > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
            <button
              ref={orphanToggleRef}
              type="button"
              className="flex w-full items-center gap-2 text-left text-sm font-medium text-amber-900"
              aria-expanded={orphansOpen}
              onClick={() => setOrphansOpen((v) => !v)}
            >
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{diagnosisTitle}</span>
              <ChevronDown
                className={cn(
                  'ml-auto h-4 w-4 transition-transform duration-200 motion-reduce:transition-none',
                  !orphansOpen && '-rotate-90'
                )}
                aria-hidden="true"
              />
            </button>
            {orphansOpen && (
              <div className="mt-2 space-y-3 text-xs text-amber-900">
                {orphanedRules.length > 0 && (
                  <div className="space-y-1.5">
                    {/* Group headings only once more than one group can appear — a
                        single heading over a single list repeats the panel title. */}
                    {showDiagnosisGroupHeadings && (
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/80">
                        {t('holdBack.orphanGroup')}
                      </p>
                    )}
                    <p>{t('holdBack.orphanDesc')}</p>
                    <ul className="space-y-1">
                      {orphanedRules.map((r) => {
                        const key = ruleKey(r.productionType, r.productType);
                        return (
                          <li key={key} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <ProductionTypeBadge
                              productionType={r.productionType}
                              className="border-amber-300 text-amber-900"
                            />
                            <span className="font-medium">{r.productType}</span>
                            <span className="tabular-nums text-amber-900/80">
                              {r.byLocation
                                .map((b) => `${locationLabelOrId(b.locationId)}: ${b.bufferUnits}`)
                                .join(', ')}
                            </span>
                            <Button
                              variant="ghost"
                              className="ml-auto h-6 gap-1 px-2 text-[11px] text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                              onClick={() => handleRemoveOrphan(r)}
                              disabled={saveHoldBackRules.isPending}
                              aria-label={t('holdBack.orphanRemoveAria', {
                                production: r.productionType,
                                type: r.productType,
                              })}
                            >
                              {removingKey === key ? (
                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                              ) : (
                                <X className="h-3 w-3" aria-hidden="true" />
                              )}
                              {t('holdBack.orphanRemove')}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {inaccessibleRows.length > 0 && (
                  <div className="space-y-1.5">
                    {showDiagnosisGroupHeadings && (
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/80">
                        {t('holdBack.inaccessibleGroup')}
                      </p>
                    )}
                    <p>{t('holdBack.inaccessibleDesc')}</p>
                    <ul className="space-y-1">
                      {inaccessibleRows.map((row) => {
                        const key = cellKey(row.locationId, row.productionType, row.productType);
                        return (
                          <li key={key} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <ProductionTypeBadge
                              productionType={row.productionType}
                              className="border-amber-300 text-amber-900"
                            />
                            <span className="font-medium">{row.productType}</span>
                            <span className="tabular-nums text-amber-900/80">
                              {`${locationLabelOrId(row.locationId)}: ${row.bufferUnits}`}
                            </span>
                            <Button
                              variant="ghost"
                              className="ml-auto h-6 gap-1 px-2 text-[11px] text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                              onClick={() => handleRemoveInaccessible(row)}
                              disabled={saveHoldBackRules.isPending}
                              aria-label={t('holdBack.inaccessibleRemoveAria', {
                                production: row.productionType,
                                type: row.productType,
                                location: locationLabelOrId(row.locationId),
                              })}
                            >
                              {removingKey === key ? (
                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                              ) : (
                                <X className="h-3 w-3" aria-hidden="true" />
                              )}
                              {t('holdBack.orphanRemove')}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Third group: the location is still the org's, but its
                    fulfilment is routed to a shared 3PL location, so the push
                    worker skips it and this number holds nothing back. Same
                    delete-only Remove as the group above — one stored cell, and
                    batching an upsert alongside it would re-arm the backend's
                    strict location gate. */}
                {delegatedRows.length > 0 && (
                  <div className="space-y-1.5">
                    {showDiagnosisGroupHeadings && (
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/80">
                        {t('holdBack.delegatedGroup')}
                      </p>
                    )}
                    <p>{t('holdBack.delegatedDesc')}</p>
                    <ul className="space-y-1">
                      {delegatedRows.map((row) => {
                        const key = cellKey(row.locationId, row.productionType, row.productType);
                        return (
                          <li key={key} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <ProductionTypeBadge
                              productionType={row.productionType}
                              className="border-amber-300 text-amber-900"
                            />
                            <span className="font-medium">{row.productType}</span>
                            <span className="tabular-nums text-amber-900/80">
                              {`${locationLabelOrId(row.locationId)}: ${row.bufferUnits}`}
                            </span>
                            <Button
                              variant="ghost"
                              className="ml-auto h-6 gap-1 px-2 text-[11px] text-amber-900 hover:bg-amber-100 hover:text-amber-900"
                              onClick={() => handleRemoveInaccessible(row)}
                              disabled={saveHoldBackRules.isPending}
                              aria-label={t('holdBack.inaccessibleRemoveAria', {
                                production: row.productionType,
                                type: row.productType,
                                location: locationLabelOrId(row.locationId),
                              })}
                            >
                              {removingKey === key ? (
                                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                              ) : (
                                <X className="h-3 w-3" aria-hidden="true" />
                              )}
                              {t('holdBack.orphanRemove')}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* One TabsContent per section value, ALL force-mounted, so every
            trigger's aria-controls resolves to a real panel. Radix only creates
            the panel id for the ACTIVE tab, so a single TabsContent would leave
            two of the three triggers pointing at nothing. The inactive two are
            empty and `hidden`; only the live one carries the grid. */}
        {SECTION_FILTERS.map((value) => (
          <TabsContent
            key={value}
            value={value}
            forceMount
            className="mt-0 space-y-6 focus-visible:outline-none"
          >
            {value === sectionFilter ? gridBody : null}
          </TabsContent>
        ))}

        {/* Legend tokens render exactly as the cells do (CELL_META_CLASS) — a
            Badge here and muted text there taught two vocabularies for one state.
            Hidden with the grid: a key to a table that isn't on screen is noise. */}
        {activeRules.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-baseline gap-1.5">
              <span className={CELL_META_CLASS}>{t('holdBack.fallbackLabel')}</span>
              <span>
                {fallbackUnits !== null
                  ? t('holdBack.legendFallbackUnits', { units: fallbackUnits })
                  : t('holdBack.legendFallback')}
              </span>
            </span>
            <span className="inline-flex items-baseline gap-1.5">
              <span className={CELL_META_CLASS}>{t('holdBack.noneLabel')}</span>
              <span>{t('holdBack.legendNone')}</span>
            </span>
            <span className="inline-flex items-baseline gap-1.5">
              <span className={CELL_META_CLASS}>{t('holdBack.noStockLabel')}</span>
              <span>{t('holdBack.legendNoStock')}</span>
            </span>
          </div>
        )}
      </Tabs>

      <HoldBackImportDialog
        open={importOpen}
        onOpenChange={(open) => {
          setImportOpen(open);
          if (!open) setImportResult(null);
        }}
        result={importResult}
        locationNames={activeLocations.map((l) => l.name)}
        onStage={handleStageImport}
        // A rejected file leaves the operator with nothing to press. Close and
        // reopen the picker in one move rather than making them find Import again.
        onChooseAnotherFile={() => {
          setImportOpen(false);
          setImportResult(null);
          fileInputRef.current?.click();
        }}
      />

      <HoldBackConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        cells={changedCells}
        locations={activeLocations.map((l) => ({ id: l.id, name: l.name }))}
        isSaving={saveHoldBackRules.isPending}
        onConfirm={handleSave}
      />

      <BackToTopPill />
    </div>
  );
}

export default function StockHoldBack() {
  return (
    <RoleGuard allowedRoles={['Super Admin', 'Admin']}>
      <StockHoldBackContent />
    </RoleGuard>
  );
}
